import React, { useEffect, useMemo, useState } from 'react';
import { Collapse } from '@mui/material';
import { Icon } from '@iconify/react';
import useTranslate from '../../../../../../customHooks/useTranslate';
import IconButton from '../../../../../../muiComponents/IconButton';
import LoadingPlaceholder from '../../../../../../muiComponents/LoadingPlaceholder';
import { commonIcons } from '../../../../../../utils/commonVariables/commonIcons';
import {
    useZitoneoContent,
    useZitoneoHotelRooms,
    useZitoneoServices,
} from '../../../../contexts/ZitoneoContentContext';
import { useZitoneoUI } from '../../../../contexts/ZitoneoUIContext';
import { VisibleContentScope } from '../../../../types/zitoneoServicesTypes';
import { HOTEL_IMAGE_TYPE_KEY_ICONS } from '../../../../utils/zitoneoServicesUtils';

const ImageTypesMenu = () => {
    const translate = useTranslate();
    const { hotelContent } = useZitoneoContent();
    const { rooms } = useZitoneoHotelRooms();
    const { ComplementaryHotelImageTypes } = useZitoneoServices();
    const {
        selectedRoomId,
        selectedImageTypeKey,
        handleSelectImageType,
        visibleContentScope,
        isLoading,
    } = useZitoneoUI();
    const [isMenuVisible, setIsMenuVisible] = useState(true);
    const selectedRoom = rooms.find(room => room.Id === selectedRoomId);
    const images =
        visibleContentScope === VisibleContentScope.ROOM
            ? selectedRoom?.HotelImages || []
            : hotelContent.HotelImages || [];
    const visibleImages = images.filter(image => !image.Deleted);

    const imageTypes = useMemo(
        () =>
            ComplementaryHotelImageTypes.map(imageType => ({
                ...imageType,
                imageCount: visibleImages.filter(
                    image => image.CategoryKey === imageType.Key
                ).length,
            })),
        [ComplementaryHotelImageTypes, visibleImages]
    );

    useEffect(() => {
        if (
            selectedImageTypeKey &&
            !imageTypes.some(
                imageType => imageType.Key === selectedImageTypeKey
            )
        ) {
            handleSelectImageType(null);
        }
    }, [imageTypes, selectedImageTypeKey, handleSelectImageType]);

    const renderImageType = (
        key: string,
        name: string,
        icon: string,
        count: number
    ) => {
        const isSelected = key
            ? selectedImageTypeKey === key
            : selectedImageTypeKey === null;
        return (
            <li
                key={key || 'all'}
                className={`categories-menu__list-item ${
                    isSelected ? '--selected' : ''
                }`}
                onClick={() => handleSelectImageType(key || null)}
            >
                <div
                    className={`category-item__icon ${
                        isSelected ? '--selected' : ''
                    }`}
                >
                    <Icon icon={icon || 'mdi:image'} />
                </div>
                <div className='category-item__main'>
                    <div className='category-item__name__container'>
                        <p>{name}</p>
                        <p>{count}</p>
                    </div>
                </div>
            </li>
        );
    };

    if (isLoading) {
        return (
            <div className='categories-menu__container'>
                <LoadingPlaceholder
                    variant='rounded'
                    width={275}
                    height={600}
                    style={{ margin: '30px 14px' }}
                />
            </div>
        );
    }

    return (
        <div
            className={`categories-menu__container ${
                isMenuVisible ? '--expanded' : '--collapsed'
            }`}
        >
            <header className='rooms-menu__header'>
                <div className='flex flex-between flex-align-center'>
                    <h5 className='rooms-menu__title'>
                        {translate('image_types')}
                    </h5>
                    <IconButton
                        iconName={
                            isMenuVisible
                                ? commonIcons.ARROW_UP
                                : commonIcons.ARROW_DOWN
                        }
                        onClick={() => setIsMenuVisible(visible => !visible)}
                        aria-expanded={isMenuVisible}
                        size='small'
                    />
                </div>
            </header>
            <Collapse
                className='categories-menu__list-collapse'
                in={isMenuVisible}
                timeout='auto'
                unmountOnExit
            >
                <ul className='categories-menu__list'>
                    {renderImageType(
                        '',
                        translate('all'),
                        'mdi:image-multiple',
                        visibleImages.length
                    )}
                    {imageTypes.map(imageType =>
                        renderImageType(
                            imageType.Key,
                            imageType.Name,
                            HOTEL_IMAGE_TYPE_KEY_ICONS[imageType.Key] ||
                                'mdi:image',
                            imageType.imageCount
                        )
                    )}
                </ul>
            </Collapse>
        </div>
    );
};

export default ImageTypesMenu;
